import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { ButtonPreset } from '@/constants/componentPresets';
import { LogLevel, useSeqLog } from '@/hooks/telemetry';
import {
  ButtonClickInvestigationEvent,
  CBUTTON_SEQ_COMPONENT,
  CLICK_INVESTIGATION_ID,
  createClickInvestigationPayload,
  resolveClickInvestigationLogLevel,
} from './buttonInvestigation';

interface UseButtonClickInvestigationInput {
  isInteractive: boolean;
  disabled: boolean;
  isLoading: boolean;
  onClick?: (event?: ReactMouseEvent<HTMLDivElement>) => void;
  preset: ButtonPreset;
  label?: string;
}

interface LogClickInvestigationEventInput {
  eventName: ButtonClickInvestigationEvent;
  logLevel: LogLevel;
}

export interface ButtonClickInvestigationHandlers {
  containerOnClick: ((event?: ReactMouseEvent<HTMLDivElement>) => void) | undefined;
  handleClickInvestigationCapture: () => void;
  handlePointerDownInvestigationCapture: () => void;
}

function useButtonClickInvestigationLogger({
  isInteractive,
  disabled,
  isLoading,
  onClick,
  preset,
  label,
}: UseButtonClickInvestigationInput): (input: LogClickInvestigationEventInput) => void {
  const clickInvestigationLog = useSeqLog(CBUTTON_SEQ_COMPONENT, {
    investigation: CLICK_INVESTIGATION_ID,
  });

  const createPayload = useCallback(() => createClickInvestigationPayload({
    isInteractive,
    disabled,
    isLoading,
    hasOnClick: Boolean(onClick),
    preset,
    label,
  }), [disabled, isInteractive, isLoading, label, onClick, preset]);

  return useCallback(({ eventName, logLevel }: LogClickInvestigationEventInput): void => {
    clickInvestigationLog(eventName, createPayload(), logLevel);
  }, [clickInvestigationLog, createPayload]);
}

export function useButtonClickInvestigation(
  input: UseButtonClickInvestigationInput,
): ButtonClickInvestigationHandlers {
  const { isInteractive, onClick } = input;
  const logClickInvestigationEvent = useButtonClickInvestigationLogger(input);

  const handleClick = useCallback((event?: ReactMouseEvent<HTMLDivElement>): void => {
    logClickInvestigationEvent({
      eventName: ButtonClickInvestigationEvent.HandleClick,
      logLevel: resolveClickInvestigationLogLevel(isInteractive),
    });

    if (isInteractive && onClick) {
      onClick(event);
    }
  }, [isInteractive, logClickInvestigationEvent, onClick]);

  const handleClickInvestigationCapture = useCallback((): void => {
    logClickInvestigationEvent({
      eventName: ButtonClickInvestigationEvent.WrapperClickCapture,
      logLevel: LogLevel.Info,
    });
  }, [logClickInvestigationEvent]);

  const handlePointerDownInvestigationCapture = useCallback((): void => {
    logClickInvestigationEvent({
      eventName: ButtonClickInvestigationEvent.WrapperPointerDownCapture,
      logLevel: LogLevel.Info,
    });
  }, [logClickInvestigationEvent]);

  return {
    containerOnClick: isInteractive ? handleClick : undefined,
    handleClickInvestigationCapture,
    handlePointerDownInvestigationCapture,
  };
}
